import { FILE_SIZE_2MB, PageTypeEnum, REGEX, TextMessage } from '@/constant';
import { ISelectedRow } from '@/hooks';
import instance, {
	ApiResponseSuccess,
	defineDocumentDownload,
} from '@/services/api';
import Resizer from 'react-image-file-resizer';

import { RcFile } from 'antd/es/upload';

import imageCompression, { Options } from 'browser-image-compression';
import { saveAs } from 'file-saver';
import { TFunction } from 'i18next';
import JSZip from 'jszip';
import * as pdfjs from 'pdfjs-dist';
import { getDocument } from 'pdfjs-dist';
import {
	DocumentInitParameters,
	TypedArray,
} from 'pdfjs-dist/types/src/display/api';
import { includes, last, split, toLower } from 'ramda';
import { isNilOrEmpty } from 'ramda-adjunct';
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
	'pdfjs-dist/build/pdf.worker.min.js',
	import.meta.url
).toString();

import { showAlert } from './alert.util';
import { formatDate } from './date.util';
import { showToast } from './toast.util';

export const convertFilePath = (fileName: string) => {
	if (!fileName) return '';
	const filePath = `${process.env.REACT_APP_FILE_URL}${fileName}`;
	return filePath;
};

export const getFilePath = (filePath: string) => {
	if (isNilOrEmpty(filePath)) return filePath;
	if (filePath.startsWith(instance.getUri())) return filePath;
	return new URL(instance.getUri()).origin + filePath;
};

export const convertHeicInWorker = (file: File): Promise<Blob> => {
	return new Promise((resolve, reject) => {
		const worker = new Worker(
			new URL('./heic-worker.util.ts', import.meta.url),
			{
				type: 'module',
			}
		);

		const id = Date.now(); // ID để phân biệt khi nhiều file

		worker.onmessage = (e) => {
			const { id: resId, blob, error } = e.data;
			if (resId !== id) return;

			worker.terminate();

			if (error) reject(error);
			else resolve(blob);
		};

		file.arrayBuffer().then((buffer) => {
			worker.postMessage({ buffer, id }, [buffer]);
		});
	});
};

export const convertAppleFileToJpgFile = async (
	input: File | Blob,
	options?: {
		type?: string;
		quality?: number;
		output?: 'file' | 'blob' | 'base64';
		createObjectURL?: boolean;
	}
): Promise<any> => {
	try {
		let blobFile = await convertHeicInWorker(input as File);

		if (options?.output === 'blob') {
			return options?.createObjectURL
				? URL.createObjectURL(blobFile as Blob)
				: blobFile;
		}

		if (options?.output === 'base64') {
			return new Promise((resolve) => {
				const reader = new FileReader();
				reader.readAsDataURL(blobFile as Blob);
				reader.onloadend = () => {
					const base64data = reader.result as string;
					resolve(base64data);
				};
			});
		}

		const fileName = (input as File).name?.split('.')?.slice(0, -1)?.join('.');
		let convertedFile = new File([blobFile as BlobPart], `${fileName}.jpeg`, {
			type: 'image/jpeg',
			lastModified: new Date().getTime(),
		});

		return convertedFile;
	} catch (error) {
		console.error('Error convertAppleFileToJpgFile: ', error);
		return input;
	}
};

export const convertPdfToImages = async (
	pdfUrl: string | URL | TypedArray | ArrayBuffer | DocumentInitParameters
) => {
	try {
		const pdf = await getDocument(pdfUrl).promise;
		const numPages = pdf.numPages;
		const images = [];

		for (let pageNum = 1; pageNum <= numPages; pageNum++) {
			const page = await pdf.getPage(pageNum);
			const viewport = page.getViewport({ scale: 1.5 });
			const canvas = document.createElement('canvas');
			const context = canvas.getContext('2d');

			canvas.width = viewport.width;
			canvas.height = viewport.height;

			if (!context) continue;

			await page.render({ canvasContext: context, viewport }).promise;

			const blob = await new Promise<Blob | null>((resolve) =>
				canvas.toBlob(resolve, 'image/png')
			);

			if (blob) {
				const url = URL.createObjectURL(blob);
				images.push(url);
			}
		}

		return images;
	} catch (error) {
		console.error('Error convertPdfToImages: ', error);
		return [];
	}
};

export const resizeImage = async (
	file: File,
	option?: {
		maxWidth?: number;
		maxHeight?: number;
		compressFormat?: string;
		quality?: number;
		rotation?: number;
	}
): Promise<File> => {
	try {
		// Check if the file is not a PDF
		if (!file.type.startsWith('image/')) {
			console.warn('Cannot resize PDF files.');
			return file;
		}

		const fileResize: File = await new Promise((resolve, reject) => {
			Resizer.imageFileResizer(
				file,
				option?.maxWidth || 1000,
				option?.maxHeight || 1000,
				option?.compressFormat || file.name?.split('.')?.pop() || 'JPEG',
				option?.quality || 100,
				option?.rotation || 0,
				(value: any) => {
					// Convert base64 to blob
					// const blobImage = dataURLtoBlob(uri);
					// // Create a new file with the blob and set the file name
					// const newFile = new File([blobImage], image.file.name, { type: "image/jpeg" });
					resolve(value);
				},
				'file'
			);
		});

		return fileResize;
	} catch (error) {
		throw new Error(`Failed to resize image: ${error}`);
	}
};

export const validateAllowFile = (
	file: File,
	regex: RegExp,
	message?: TextMessage | string,
	isAlert?: boolean,
	alert6?: string
) => {
	if (!file?.name?.toLocaleLowerCase().match(regex)) {
		if (isAlert) {
			showAlert(message || alert6 || TextMessage.ALERT_6);
		} else {
			showToast(message || TextMessage.COMMON_18, { type: 'ERROR' });
		}
		return false;
	}
	return true;
};

export const isFileTypeInvalid = (accept: string, fileName: string) => {
	if (isNilOrEmpty(accept) || isNilOrEmpty(fileName)) return false;
	const fileExtension = fileName.split('.').pop()?.toLocaleLowerCase();

	if (fileExtension && accept.indexOf(fileExtension) === -1) {
		return true;
	}

	return false;
};

export const getBase64 = (file: RcFile | File): Promise<string> =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onload = () => resolve(reader.result as string);
		reader.onerror = (error) => reject(error);
	});

export const onDownloadFileLocal = async (item: File | null) => {
	if (!item) return;
	try {
		const filePath = URL.createObjectURL(item);
		const fileName = item.name;
		await fetch(filePath)
			.then((response) => response.blob())
			.then((blob) => {
				const url = URL.createObjectURL(new Blob([blob]));
				const link = document.createElement('a');
				link.href = url;
				link.download = fileName;
				document.body.appendChild(link);
				link.click();
				URL.revokeObjectURL(filePath);
				link.remove();
			});
	} catch (error) {
		console.error('Error onDownloadFile: ', error);
	}
};

export const onDownloadFileFromServer = async (
	pathName: string,
	fileName?: string,
	options?: {
		output?: 'file' | 'blob' | 'base64';
		createObjectURL?: boolean;
		prefixPath?: string;
		downloadReason?: string;
		downloadFileName?: string;
	}
) => {
	try {
		if (isNilOrEmpty(pathName)) return '';

		const {
			output,
			createObjectURL,
			prefixPath,
			downloadReason,
			downloadFileName,
		} = options || {};

		const lastPathname = getFileNameFromPath(pathName, prefixPath) || '';

		const res = await defineDocumentDownload({
			path: lastPathname,
			downloadReason,
			downloadFileName,
		});

		if (res?.status === 200) {
			const blob = res.data as Blob;
			if (output === 'blob') {
				return createObjectURL ? URL.createObjectURL(blob) : blob;
			}
			if (output === 'base64') {
				return new Promise((resolve) => {
					const reader = new FileReader();
					reader.readAsDataURL(blob);
					reader.onloadend = () => {
						const base64data = reader.result as string;
						resolve(base64data);
					};
				});
			}

			const file = new File([blob], fileName || lastPathname);
			if (output === 'file') {
				return file;
			}
			onDownloadFileLocal(file);
		}
	} catch (error) {
		console.error('Error onDownloadFileFromServer: ', error);
		return undefined;
	}
};

export const getFileNameFromPath = (
	filePath: string | null | undefined,
	prefix = 'document/download/'
) => {
	if (!filePath) return '';
	// dynamic file path, include date time
	// ex: api/common/document/download/2025/02/26/abcd.jpg

	const parts = filePath.split(prefix);

	if (parts.length > 1) {
		return parts[1];
	} else {
		return last(filePath.split('/'));
	}
};

export const getFileNameFromContentDisposition = (disposition: string) => {
	let filename = 'downloaded_file';

	if (!disposition) return filename;

	let filenameMatch = null;

	const filenameStarRegex = /filename\*=([^;]+);?/i;
	const matches = filenameStarRegex.exec(disposition);

	if (matches != null && matches[1]) {
		filenameMatch = matches[1];

		const utf8FilenameRegex = /UTF-8''(.*)/i;
		const utf8Matches = utf8FilenameRegex.exec(filenameMatch);

		if (utf8Matches != null && utf8Matches[1]) {
			filename = decodeURIComponent(utf8Matches[1]);
		} else {
			filename = decodeURIComponent(filenameMatch);
		}
	} else {
		const filenameRegex = /filename=([^;]+);?/i;
		const filenameMatches = filenameRegex.exec(disposition);
		if (filenameMatches != null && filenameMatches[1]) {
			filename = filenameMatches[1].replace(/['"]/g, '');
		}
	}

	const invalidCharacters = /[\\/:*?"<>|]/g;
	filename = filename.replace(invalidCharacters, '_');

	return filename;
};

export const getZipFileName = (pageType: PageTypeEnum) => {
	switch (pageType) {
		case PageTypeEnum.APPLICANT:
			return '근로자';
		case PageTypeEnum.UNAUTHORIZED_DEPARTURE:
			return '무단이탈';
		case PageTypeEnum.EXTENSION_STAY:
			return '체류연장';
		case PageTypeEnum.FRANCHISE:
			return '고용주';
		case PageTypeEnum.LODGING:
			return '숙소';
		case PageTypeEnum.PROGRAM:
			return '프로그램';
		case PageTypeEnum.RECOMMENDER:
			return '추천자';
		case PageTypeEnum.CHANGE_WORK_PLACE:
			return '근무처변경';
		case PageTypeEnum.UNIT:
			return '사업체정보';
		case PageTypeEnum.FILE:
			return '계절 근로 관련 서류';

		default:
			break;
	}
};

// use for file name
export const renameDuplicates = (fileNames: string[]): string[] => {
	const nameCount: Record<string, number> = {};
	const renamedFiles: string[] = [];

	fileNames.forEach((fileName) => {
		const fileParts = fileName.split('.');
		const name = fileParts.slice(0, -1).join('.');
		const extension = fileParts.slice(-1)[0];

		if (nameCount[name] !== undefined) {
			nameCount[name] += 1;
			renamedFiles.push(`${name}(${nameCount[name]}).${extension}`);
		} else {
			nameCount[name] = 0;
			renamedFiles.push(fileName);
		}
	});

	return renamedFiles;
};
// use for folder name
export const countNameOccurrences = (arr: ISelectedRow[]) => {
	return arr.reduce((acc: Record<string, number>, item: ISelectedRow) => {
		acc[item.name] = (acc[item.name] || 0) + 1;
		return acc;
	}, {});
};

const addPrefixForApplicant = ({
	fileName,
	startFileName,
	pageType,
}: {
	fileName: string;
	startFileName: string;
	pageType: PageTypeEnum;
}) => {
	if (!fileName.startsWith(startFileName)) {
		return `${startFileName}_${fileName}`;
	}
	return fileName;
};

export const downloadDocuments = async ({
	files,
	pageType,
	zipFileName,
}: {
	files: ApiResponseSuccess<Blob>[];
	pageType: PageTypeEnum;
	zipFileName: string;
}) => {
	if (files.length === 1) {
		const fileName = addPrefixForApplicant({
			fileName: getFileNameFromContentDisposition(
				files[0].headers['content-disposition']
			),
			startFileName: zipFileName,
			pageType,
		});

		const href = URL.createObjectURL(files[0]?.data);
		const a = document.createElement('a');
		a.href = href;
		a.download = fileName || '';
		a.click();
		window.URL.revokeObjectURL(href);
	} else {
		const zip = new JSZip();
		const fileNames = files.map((file) =>
			addPrefixForApplicant({
				fileName: getFileNameFromContentDisposition(
					file.headers['content-disposition']
				),
				startFileName: zipFileName,
				pageType,
			})
		);

		// change name of duplicated files
		const uniqueFileNames = renameDuplicates(fileNames);

		files.forEach((file, index) => {
			const fileName = uniqueFileNames[index];
			zip.file(fileName || '', file.data);
		});

		const content: Blob = await zip.generateAsync({ type: 'blob' });
		const today = formatDate(new Date(), { outputFormat: 'YYYYMMDD' });

		saveAs(content, `${zipFileName}_${today}.zip`);
	}
};

const getFileFolderPath = (
	file: Blob,
	fileName: string,
	folder: string | string[]
) => {
	if (!fileName) {
		return '';
	}

	if (isNilOrEmpty(folder)) {
		return { file, filePath: fileName };
	}

	if (Array.isArray(folder)) {
		return folder.map((f: string) => ({ file, filePath: `${f}/${fileName}` }));
	}

	return { file, filePath: `${folder}/${fileName}` };
};

export const downloadZipFolderDocuments = async ({
	files: resFiles,
	pageType,
}: {
	files: ApiResponseSuccess<Blob>[];
	pageType: PageTypeEnum;
}) => {
	const zip = new JSZip();
	let files: Array<{ file: Blob; filePath: string }> = [];
	resFiles.map((file) => {
		const { params } = JSON.parse(file.config.data);
		const fileName =
			params?.fileName ||
			getFileNameFromContentDisposition(file.headers['content-disposition']);
		const fileWithPath = getFileFolderPath(file.data, fileName, params?.folder);
		if (fileWithPath) {
			if (Array.isArray(fileWithPath)) {
				files = [...files, ...fileWithPath];
			} else {
				files = [...files, fileWithPath];
			}
		}
	});

	// organize files by folder
	const folderFiles: { [folder: string]: Array<{ file: Blob; name: string }> } =
		{};
	files.forEach((item) => {
		const folder = item.filePath.substring(0, item.filePath.lastIndexOf('/'));
		const fileName = item.filePath.substring(
			item.filePath.lastIndexOf('/') + 1
		);

		if (!folderFiles[folder]) {
			folderFiles[folder] = [];
		}
		folderFiles[folder].push({ file: item.file, name: fileName });
	});

	// handle each folder and rename duplicate files
	Object.entries(folderFiles).forEach(([folder, filesInFolder]) => {
		// const fileNames = filesInFolder.map((f) => f.name);
		const fileNames = filesInFolder.map((f) => {
			const fileName = addPrefixForApplicant({
				fileName: f.name,
				startFileName: folder,
				pageType,
			});
			return fileName;
		});

		const uniqueNames = renameDuplicates(fileNames);

		filesInFolder.forEach((file, index) => {
			zip.file(`${folder}/${uniqueNames[index]}`, file.file);
		});
	});

	const content: Blob = await zip.generateAsync({ type: 'blob' });
	const fileZipName = getZipFileName(pageType);
	let outputFormat = 'YYYYMMDD';
	if (pageType === PageTypeEnum.FILE) {
		outputFormat = 'YYYYMMDDHHmm';
	}
	const today = formatDate(new Date(), { outputFormat });

	saveAs(content, `${fileZipName}_${today}.zip`);
};

export const downloadSingleDocument = async ({
	files,
}: {
	files: ApiResponseSuccess<Blob>[];
}) => {
	const fileName = getFileNameFromContentDisposition(
		files[0].headers['content-disposition']
	);
	onDownloadFileLocal(new File([files[0].data], fileName));
};

/**
 * @description Function to handle duplicate file names.
 * @param {string} name - The `name` of the file.
 * @param {Set<string>} existingNames - The `Set` of existing file names.
 * @returns {string}
 */
export const getUniqueFilename = (name: string, existingNames: Set<string>) => {
	if (!existingNames.has(name)) {
		existingNames.add(name);
		return name;
	}

	let counter = 1;
	let newName;
	do {
		newName = `${name.replace(/\.png$/, '')}(${counter}).png`;
		counter++;
	} while (existingNames.has(newName));

	existingNames.add(newName);
	return newName;
};

export const compressImage = async (
	file: File | null | undefined,
	option: Options = { maxSizeMB: FILE_SIZE_2MB }
): Promise<File | undefined> => {
	if (!file) return undefined;
	if (!isImage(file)) return file;

	const maxSizeMB = option?.maxSizeMB ?? FILE_SIZE_2MB;
	if (!shouldCompress(file, maxSizeMB)) return file;

	return await compressFile(file, { maxSizeMB, ...option });
};

// check file is image
const isImage = (file: File): boolean => {
	if (!file.type.startsWith('image/')) {
		return false;
	}
	return true;
};

// check file is need compress
export const shouldCompress = (file: File, maxSizeMB: number): boolean => {
	return file.size > maxSizeMB * 1024 * 1024;
};

// compress file and convert Blob to File
const compressFile = async (file: File, options: Options): Promise<File> => {
	try {
		if (!imageCompression) {
			throw new Error('[compressImage] imageCompression is not available');
		}
		let compressedBlob = await imageCompression(file, {
			useWebWorker: true,
			alwaysKeepResolution: true,
			maxWidthOrHeight: 1920,
			...options,
		});

		// if compressed file is still larger than maxSizeMB, continue to compress
		let quality = 0.9;
		const maxSizeMB = options.maxSizeMB ?? FILE_SIZE_2MB;
		while (compressedBlob.size > maxSizeMB * 1024 * 1024 && quality > 0.2) {
			quality -= 0.1;
			compressedBlob = await imageCompression(file, {
				useWebWorker: true,
				alwaysKeepResolution: true,
				maxSizeMB: options.maxSizeMB,
				maxWidthOrHeight: 1920,
				initialQuality: quality,
			});
		}

		// convert Blob to File
		return new File([compressedBlob], file.name, {
			type: file.type,
			lastModified: Date.now(),
		});
	} catch (error) {
		console.error('[compressImage] Image compression failed:', error);
		throw new Error('Image compression failed. Please try again.');
	}
};

export const isBase64 = (str: string) => {
	try {
		return (
			str.startsWith('data:') ||
			str.startsWith('blob:') ||
			REGEX.IS_BASE64.test(str)
		);
	} catch (e) {
		return false;
	}
};

export const getImageType = (item: any) => {
	const isRemoteFile = item?.fileId;

	if (isRemoteFile) {
		const names = split('.', item?.fileName || '');
		const type = toLower(last(names) as string);

		return {
			isPDFFile: type === 'pdf',
			isZipFile: type === 'zip',
			isHeicHeifFile: includes(type, ['heic', 'heif']),
			isHeifFile: type === 'heif',
		};
	}
	const names = split('.', item?.name || '');
	const typeByName = toLower(last(names) as string);

	const isPDFFile =
		item?.type === 'application/pdf' ||
		(isNilOrEmpty(item?.type) && includes(typeByName, 'pdf'));
	const isZipFile =
		includes(item?.type, ['application/zip', 'application/x-zip-compressed']) ||
		(isNilOrEmpty(item?.type) && includes(typeByName, 'zip'));
	const isHeicHeifFile =
		includes(item?.type, ['image/heic', 'image/heif']) ||
		(isNilOrEmpty(item?.type) && includes(typeByName, ['heif', 'heic']));
	const isHeifFile =
		includes(item?.type, ['image/heif']) ||
		(isNilOrEmpty(item?.type) && includes(typeByName, 'heif'));

	return {
		isPDFFile,
		isZipFile,
		isHeicHeifFile,
		isHeifFile,
	};
};

export const isHeicHeifFile = (fileName: string): boolean => {
	const heicHeifRegex = /\.(heic|heif)$/i;
	return heicHeifRegex.test(fileName);
};

export const convertAndCompressImages = async (
	files: File[],
	t: TFunction<'validation' | 'documentArea' | 'message'>,
	isShowAlertHasHeicHeif = true
): Promise<File[]> => {
	try {
		// Check for HEIC/HEIF files first and show alert if needed
		const hasHeicHeif = files.some((file) => isHeicHeifFile(file.name));
		if (hasHeicHeif && isShowAlertHasHeicHeif) {
			showAlert(t('validation:has-heic-heif-need-convert-format'));
		}

		// Then process the files
		return await Promise.all(
			files.map(async (file) => {
				if (isHeicHeifFile(file.name)) {
					const convertedFile = await convertAppleFileToJpgFile(file);
					return (await compressImage(convertedFile)) || file;
				}
				return file;
			})
		);
	} catch (error) {
		console.error('Error processing files:', error);
		return files; // Return original files if processing fails
	}
};
